import {Box} from "@mui/material";
import {SxProps} from "@mui/system";
import React, {useCallback, useRef} from "react";
import {env} from "../../../../constants";
import {PaymentDetails, PaymentType} from "../../../declarations";

export type FormValues = {
  email?: string;
  confirmTerms: boolean;
};

export type ApplePayButtonStyle = "white" | "white-outline" | "black";

interface ApplePayButtonClickProps {
  totalAmount: string;
  onCardSubmit: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  isChangePaymentMethod: boolean;
  merchantSiteId: string;
  formValues: FormValues;
  label: string;
  sessionActiveRef: React.MutableRefObject<boolean>;
}

const convertApplePayFormToPaymentDetails = (
  formValues: any,
  trxData: any,
  applePayCompletionFunction: any
): PaymentDetails => {
  return {
    apple_pay: trxData,
    email: formValues.email || undefined,
    terms_and_conditions: formValues.confirmTerms,
    applePayCompletionFunction: applePayCompletionFunction,
  };
};

const onApplePayButtonClicked = async ({
  totalAmount,
  onCardSubmit,
  isChangePaymentMethod,
  merchantSiteId,
  formValues,
  label,
  sessionActiveRef,
}: ApplePayButtonClickProps) => {
  // Prevent starting a new session if one is already active
  if (sessionActiveRef.current) {
    return;
  }

  const isProduction = ["production"].includes(env);
  const paymentRequest = {
    merchantSiteId: Number(merchantSiteId),
    env: isProduction ? "prod" : "int",
    applicationData: btoa(merchantSiteId),
    countryCode: "US",
    currencyCode: "USD",
    supportedNetworks: ["visa", "masterCard", "amex", "discover"],
    total: {
      label: label,
      type: "final",
      amount: totalAmount,
    },
    oncancel: () => {
      sessionActiveRef.current = false;
    },
  };
  const postTransactionData = function (trxData: any, applePayCompletionFunction: any) {
    const paymentDetails = convertApplePayFormToPaymentDetails(formValues, trxData, applePayCompletionFunction);
    onCardSubmit("ApplePay", paymentDetails, isChangePaymentMethod);
    sessionActiveRef.current = false;
  };
  // @ts-ignore
  const session = sfc.applePay.buildSession(paymentRequest, (result: any, completion: any) => {
    postTransactionData(result, completion);
  });

  sessionActiveRef.current = true;
  session.begin();
};

export interface ApplePayButtonProps {
  onCardSubmit: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  totalAmount: string;
  merchantSiteId: string;
  isChangePaymentMethod: boolean;
  buttonStyle?: ApplePayButtonStyle;
  disabled?: boolean;
  sx?: SxProps;
  formValues: FormValues;
  label: string;
  buttonRadius: string;
}

export const ApplePayButton: React.FC<ApplePayButtonProps> = ({
  onCardSubmit,
  merchantSiteId,
  isChangePaymentMethod = false,
  totalAmount,
  buttonStyle = "black",
  disabled = false,
  sx,
  formValues,
  label,
  buttonRadius,
}) => {
  const isImmediatePayment = totalAmount != "0.00";
  const sessionActiveRef = useRef(false);
  const handleClick = useCallback(() => {
    onApplePayButtonClicked({formValues, totalAmount, onCardSubmit, isChangePaymentMethod, merchantSiteId, label, sessionActiveRef});
  }, [totalAmount, formValues]);

  const baseStyles = {
    display: "inline-block",
    WebkitAppearance: "-apple-pay-button" as any,
    ApplePayButtonType: isImmediatePayment ? "continue" : "set-up",
    ApplePayButtonStyle: buttonStyle,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  } as const;

  return (
    <>
      <Box
        component="button"
        className="ApplePayButton"
        sx={{
          ...baseStyles,
          borderRadius: buttonRadius,
          width: "100%",
          height: "48px",
          ...sx,
        }}
        onClick={handleClick}
        disabled={disabled}
        type="button"
      />
    </>
  );
};
